/**
 * Utility functions for matching treasure names with robust comparison
 * Handles extra spaces, different spacing, and case differences
 */

/**
 * Normalizes a string for comparison by:
 * - Converting to lowercase
 * - Removing extra whitespace
 * - Trimming leading/trailing spaces
 * @param {string} str - The string to normalize
 * @returns {string} - Normalized string
 */
export const normalizeString = (str) => {
  if (!str || typeof str !== "string") return "";
  return str.toLowerCase().replace(/\s+/g, " ").trim();
};

/**
 * Checks if two strings match after normalization
 * @param {string} str1 - First string to compare
 * @param {string} str2 - Second string to compare
 * @returns {boolean} - True if strings match after normalization
 */
export const stringsMatch = (str1, str2) => {
  if (!str1 || !str2) return false;
  return normalizeString(str1) === normalizeString(str2);
};

/**
 * Finds a treasure by name using robust comparison
 * @param {Array} treasureData - Array of treasure objects
 * @param {string} targetName - The name to search for
 * @returns {Object|false} - The matching treasure object or false if not found
 */
export const findTreasureByName = (treasureData, targetName) => {
  if (!treasureData || !Array.isArray(treasureData) || !targetName) {
    return false;
  }

  const normalizedTarget = normalizeString(targetName);

  return (
    treasureData.find((treasure) => {
      if (!treasure || !treasure.name) return false;

      const normalizedTreasureName = normalizeString(treasure.name);

      // Exact match after normalization
      if (normalizedTreasureName === normalizedTarget) {
        return true;
      }

      // Check if target contains the treasure name (for partial matches)
      if (normalizedTarget.includes(normalizedTreasureName)) {
        return true;
      }

      // Check if treasure name contains the target (for partial matches)
      if (normalizedTreasureName.includes(normalizedTarget)) {
        return true;
      }

      return false;
    }) || false
  );
};

/**
 * Alternative matching strategy that's more flexible
 * @param {Array} treasureData - Array of treasure objects
 * @param {string} targetName - The name to search for
 * @returns {Object|false} - The matching treasure object or false if not found
 */
export const findTreasureByNameFlexible = (treasureData, targetName) => {
  if (!treasureData || !Array.isArray(treasureData) || !targetName) {
    return false;
  }

  const normalizedTarget = normalizeString(targetName);

  // First try exact match
  let match = treasureData.find(
    (treasure) =>
      treasure &&
      treasure.name &&
      normalizeString(treasure.name) === normalizedTarget
  );

  if (match) return match;

  // Then try contains match
  match = treasureData.find(
    (treasure) =>
      treasure &&
      treasure.name &&
      (normalizedTarget.includes(normalizeString(treasure.name)) ||
        normalizeString(treasure.name).includes(normalizedTarget))
  );

  if (match) return match;

  // Finally try fuzzy matching for close matches
  return (
    treasureData.find((treasure) => {
      if (!treasure || !treasure.name) return false;

      const treasureName = normalizeString(treasure.name);
      const target = normalizedTarget;

      // Check if words match (ignoring order and extra spaces)
      const treasureWords = treasureName
        .split(/\s+/)
        .filter((word) => word.length > 0);
      const targetWords = target.split(/\s+/).filter((word) => word.length > 0);

      // If either has no words, fall back to simple contains
      if (treasureWords.length === 0 || targetWords.length === 0) {
        return treasureName.includes(target) || target.includes(treasureName);
      }

      // Check if most words match
      const matchingWords = targetWords.filter((word) =>
        treasureWords.some(
          (treasureWord) =>
            treasureWord.includes(word) || word.includes(treasureWord)
        )
      );

      // Return true if more than 50% of words match
      return matchingWords.length / targetWords.length > 0.5;
    }) || false
  );
};
